import { Container, Graphics } from 'pixi.js';
import { rnd } from './util';

const BIOME_TIERS = [
  { hi: 4, prim: 0x4488ff, sec: 0x88aaff }, // blue/cyan
  { hi: 9, prim: 0xaa44ff, sec: 0xff44ff }, // purple/magenta
  { hi: 14, prim: 0xff4400, sec: 0xff8800 }, // red/orange
  { hi: 999, prim: 0xffcc00, sec: 0xffffaa }, // gold/white
];

export function biomePrim(wave: number): number {
  for (const b of BIOME_TIERS) if (wave <= b.hi) return b.prim;
  return BIOME_TIERS[BIOME_TIERS.length - 1].prim;
}

interface Star {
  x: number; y: number;
  speed: number;
  size: number;
  alpha: number;
  twinkleT: number; twinkleRate: number;
  layer: 0 | 1 | 2;
  view: Graphics;
}

export class Starfield {
  private stars: Star[] = [];
  private nebulae: Graphics[] = [];
  private currentTint = 0xffffff;
  private boostStretch = 0;

  constructor(stage: Container) {
    const root = new Container();
    stage.addChild(root);

    // Parallax nebula layer (behind everything)
    const nebLayer = new Container();
    root.addChild(nebLayer);
    for (let i = 0; i < 4; i++) {
      const r = rnd(400, 700);
      const v = new Graphics();
      v.circle(0, 0, r);
      v.fill({ color: 0x4488ff, alpha: rnd(0.04, 0.1) });
      v.x = rnd(0, window.innerWidth);
      v.y = rnd(0, window.innerHeight);
      nebLayer.addChild(v);
      this.nebulae.push(v);
    }

    // Layer 0 — deep (slow, large, dim, blueish)
    for (let i = 0; i < 32; i++) this.spawnStar(root, 0);
    // Layer 1 — mid (medium speed/size, white)
    for (let i = 0; i < 78; i++) this.spawnStar(root, 1);
    // Layer 2 — near (fast, small bright + streak)
    for (let i = 0; i < 56; i++) this.spawnStar(root, 2);
  }

  private spawnStar(root: Container, layer: 0 | 1 | 2): void {
    const v = new Graphics();
    let speed: number, size: number, alpha: number;
    if (layer === 0) {
      speed = rnd(8, 22);
      size = rnd(2.0, 3.4);
      alpha = rnd(0.18, 0.35);
      v.circle(0, 0, size);
      v.fill({ color: 0xaaccff, alpha: 1 });
    } else if (layer === 1) {
      speed = rnd(28, 60);
      size = rnd(1.0, 2.0);
      alpha = rnd(0.4, 0.75);
      v.circle(0, 0, size);
      v.fill({ color: 0xffffff, alpha: 1 });
    } else {
      speed = rnd(70, 130);
      size = rnd(0.6, 1.4);
      alpha = rnd(0.7, 1.0);
      v.circle(0, 0, size);
      v.fill({ color: 0xffffff, alpha: 1 });
    }
    v.alpha = alpha;
    root.addChild(v);
    this.stars.push({
      x: rnd(0, window.innerWidth),
      y: rnd(0, window.innerHeight),
      speed, size, alpha,
      twinkleT: rnd(0, Math.PI * 2),
      twinkleRate: layer === 0 ? rnd(0.4, 0.9) : layer === 1 ? rnd(0.8, 1.6) : rnd(1.4, 2.6),
      layer,
      view: v,
    });
  }

  setBoostStretch(s: number): void {
    this.boostStretch = Math.max(0, Math.min(1, s));
  }

  update(dt: number, W: number, H: number, wave = 1): void {
    // Biome tint lerp on nebula
    const tint = biomePrim(wave);
    if (tint !== this.currentTint) {
      this.currentTint = lerpColor(this.currentTint, tint, Math.min(1, dt * 1.0));
      for (const n of this.nebulae) n.tint = this.currentTint;
    }
    for (const n of this.nebulae) {
      n.x -= 6 * dt;
      if (n.x < -700) n.x = W + 700;
    }

    const stretch = this.boostStretch;
    for (const s of this.stars) {
      s.x -= s.speed * dt * (1 + stretch * (s.layer === 2 ? 1.6 : s.layer === 1 ? 0.6 : 0.2));
      if (s.x < -8) {
        s.x = W + 8;
        s.y = rnd(0, H);
      }
      s.view.x = s.x;
      s.view.y = s.y;
      // Twinkle alpha
      s.twinkleT += dt * s.twinkleRate;
      const tw = 0.7 + 0.3 * Math.sin(s.twinkleT);
      s.view.alpha = s.alpha * tw;
      // Streak: scale near stars horizontally during boost
      if (s.layer === 2 && stretch > 0) {
        s.view.scale.x = 1 + stretch * 6;
      } else if (s.view.scale.x !== 1) {
        s.view.scale.x = 1;
      }
    }
  }
}

function lerpColor(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 0xff, ag = (a >> 8) & 0xff, ab = a & 0xff;
  const br = (b >> 16) & 0xff, bg = (b >> 8) & 0xff, bb = b & 0xff;
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const c = Math.round(ab + (bb - ab) * t);
  return (r << 16) | (g << 8) | c;
}
